import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import { resetBranding } from '../../../lib/branding'
import { useMyCompanies } from '../../../hooks/useMyCompanies'
import { MOBILE_MORE } from './mobileNav'
import MobileLauncherGrid from './MobileLauncherGrid'
import { useMobileV2, setMobileV2 } from '../../../lib/flags'

interface Props {
  open: boolean
  onClose: () => void
  isAdmin: boolean
  cadastroRecAvailable: boolean
  userEmail?: string | null
}

/** Bottom sheet com o resto das telas + sair. Desliza de baixo. */
export default function MobileMoreSheet({ open, onClose, isAdmin, cadastroRecAvailable, userEmail }: Props) {
  useEffect(() => {
    if (!open) return
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  const v2 = useMobileV2()

  if (!open) return null

  const items = MOBILE_MORE
    .filter(i => !i.adminOnly || isAdmin)
    .filter(i => !i.cadastroGated || cadastroRecAvailable)

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end no-tap-highlight" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div className="relative bg-surface border-t border-brand-700/40 rounded-t-2xl shadow-2xl pb-safe animate-[slideUp_0.2s_ease-out]">
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-line" />
        </div>

        {v2 ? (
          <div className="px-3 pb-2 pt-1">
            <MobileLauncherGrid isAdmin={isAdmin} cadastroRecAvailable={cadastroRecAvailable} onNavigate={onClose} />
          </div>
        ) : (
          <div className="px-3 pb-2 grid grid-cols-2 gap-2">
            {items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-3 rounded-xl border transition-colors touch-action-manip ${
                    isActive
                      ? 'bg-brand-700/40 border-brand-500 text-white'
                      : 'bg-sunken/60 border-line text-ink-muted active:bg-sunken'
                  }`
                }
              >
                <span className="text-xl leading-none">{item.icon}</span>
                <span className="text-sm font-medium leading-tight">{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}

        <CompanySection />

        <div className="px-3 pt-1 pb-3 border-t border-line mt-1">
          {userEmail && (
            <p className="text-[11px] text-ink-faint truncate px-1 mb-2">{userEmail}</p>
          )}
          {isAdmin && (
            <button
              onClick={() => setMobileV2(!v2)}
              className="w-full mb-2 py-2 rounded-xl bg-sunken/60 active:bg-sunken text-ink-faint text-xs font-medium touch-action-manip flex items-center justify-center gap-1.5"
            >
              🧪 Novo visual mobile: <span className={v2 ? 'text-brand-500 font-bold' : ''}>{v2 ? 'ON' : 'OFF'}</span>
            </button>
          )}
          <button
            onClick={async () => { resetBranding(); await supabase.auth.signOut() }}
            className="w-full py-3 rounded-xl bg-sunken active:bg-line text-ink-muted text-sm font-medium touch-action-manip"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  )
}

function CompanySection() {
  const { companies, activeCompanyId, loading } = useMyCompanies()
  const [switching, setSwitching] = useState(false)
  const [expanded, setExpanded] = useState(false)

  if (loading || companies.length <= 1) return null

  const active = companies.find(c => c.companyId === activeCompanyId)

  const select = async (companyId: string) => {
    if (companyId === activeCompanyId || switching) return
    setSwitching(true)
    const { error } = await supabase.rpc('switch_active_company', { p_company_id: companyId })
    if (error) { setSwitching(false); return }
    window.location.reload()
  }

  return (
    <div className="px-3 pt-2 pb-1 border-t border-line">
      <p className="text-[10px] text-ink-faint uppercase tracking-wider px-1 mb-1.5">Empresa</p>
      <button
        onClick={() => setExpanded(e => !e)}
        disabled={switching}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-sunken/60 border border-line active:bg-sunken text-sm text-ink disabled:opacity-50 touch-action-manip"
      >
        <span className="truncate">{active?.name ?? 'Selecionar empresa'}</span>
        <span className="text-ink-muted">{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded && (
        <div className="mt-1.5 rounded-xl border border-line bg-paper overflow-hidden">
          {companies.map(c => (
            <button
              key={c.companyId}
              onClick={() => select(c.companyId)}
              disabled={switching}
              className={`w-full text-left px-3 py-2.5 text-sm border-b border-line last:border-b-0 active:bg-sunken touch-action-manip ${
                c.companyId === activeCompanyId ? 'text-brand-400 font-semibold' : 'text-ink-muted'
              }`}
            >
              {c.name}
              {c.companyId === activeCompanyId && <span className="ml-2 text-[10px] text-ink-faint">(atual)</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
